import React from 'react';
import classNames from 'classnames';
import styles from './Badge.module.scss';
import Icon, { IconName } from '../Icon/Icon';
import { useOptionalTheme } from '../../../providers/ThemeProvider';

export enum BadgeType {
  Neutral = 'neutral',
  Successful = 'succesful',
  Warning = 'warning',
  Error = 'error',
  Neutral2 = 'neutral2',
  Neutral3 = 'neutral3',
  Neutral4 = 'neutral4',
  Neutral5 = 'neutral5',
  Neutral6 = 'neutral6',
  Neutral7 = 'neutral7',
}

// The backend sends status colours as one of the twelve palette values from
// df_clientportal_api/utility/color_mapping.py. In dark mode those map onto the
// themed badge types, which have lighter text and darker tints; in light mode
// the backend colour is rendered as delivered.
export const BACKEND_PALETTE: Record<string, BadgeType> = {
  '#D9433A': BadgeType.Error,
  '#F07068': BadgeType.Error,
  '#E0A100': BadgeType.Warning,
  '#F2B935': BadgeType.Neutral3,
  '#1E9E6A': BadgeType.Successful,
  '#66C5DB': BadgeType.Neutral6,
  '#36A9E1': BadgeType.Neutral4,
  '#2F3A8B': BadgeType.Neutral7,
  '#5436AF': BadgeType.Neutral2,
  '#A08CE8': BadgeType.Neutral2,
  '#C24BB5': BadgeType.Neutral5,
};

export const resolvePaletteBadgeType = (color?: string): BadgeType | undefined =>
  color ? BACKEND_PALETTE[color.trim().toUpperCase()] : undefined;

export interface BadgeProps {
  label: string;
  type?: BadgeType | string;
  transparent?: boolean;
  showDot?: boolean;
  showBg?: boolean;
  dotFilled?: boolean;
  expanded?: boolean;
  hideLabel?: boolean;
  icon?: IconName;
  className?: string;
  onClick?: () => void;
  preLabel?: string;
  colorOverride?: string;
  iconFirst?: boolean;
  hoverText?: string;
}
const Badge: React.FC<BadgeProps> = ({
  label,
  icon,
  onClick,
  type = BadgeType.Neutral,
  transparent = false,
  showBg = true,
  showDot = false,
  dotFilled = false,
  expanded = false,
  hideLabel = false,
  className = '',
  preLabel,
  colorOverride,
  iconFirst = false,
  hoverText,
}) => {
  const theme = useOptionalTheme()?.theme ?? 'light';
  const paletteType = theme === 'dark' ? resolvePaletteBadgeType(colorOverride) : undefined;
  const badgeClasses = classNames('badge', {
    [paletteType ?? type]: !transparent,
    truncate: true,
  });

  const inlineStyles: React.CSSProperties = {};
  if (colorOverride && !paletteType) {
    inlineStyles.backgroundColor = colorOverride.startsWith('var(')
      ? `color-mix(in srgb, ${colorOverride} 16%, transparent)`
      : `${colorOverride}33`;
    inlineStyles.color = colorOverride;
  }

  const content = (
    <span
      title={hoverText}
      style={inlineStyles}
      className={classNames(styles.root, 'rounded-sm w-max', badgeClasses, !expanded && styles.circle, {
        [styles.hasIcon]: icon,
        [styles.hideBg]: !showBg,
      })}>
      {(showDot || !expanded) && (
        <span
          className={classNames(styles.dot, {
            [styles.filled]: dotFilled || !expanded,
          })}
        />
      )}
      {preLabel && <span className={classNames('typo-text-12--bold', 'mr-4')}>{preLabel}</span>}
      {icon && iconFirst && <Icon className={`${!hideLabel && label && 'mr-6'}`} name={icon} width={18} height={18} />}
      {!hideLabel && expanded && <span className={classNames('typo-text-12--bold', showDot && 'ml-4')}>{label}</span>}
      {icon && !iconFirst && <Icon className={`${!hideLabel && label && 'ml-6'}`} name={icon} width={18} height={18} />}
    </span>
  );

  return onClick ? (
    <button onClick={onClick} type="button">
      {content}
    </button>
  ) : (
    <div className={className}>{content}</div>
  );
};

export default Badge;
